import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Chip, Button, Skeleton, CircularProgress, Alert } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightCard from './InsightCard';
import InsightService from '../../services/InsightService';

const FILTERS = [
    { key: 'ALL',         label: 'All',         color: '#427898' },
    { key: 'CRITICAL',    label: 'Critical',    color: '#EF4444' },
    { key: 'WARNING',     label: 'Warning',     color: '#F59E0B' },
    { key: 'OPPORTUNITY', label: 'Opportunity', color: '#10B981' },
    { key: 'DISMISSED',   label: 'Dismissed',   color: '#94A3B8' },
];

function SkeletonCard() {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2, bgcolor: '#F1F5F9' }} />
        </Box>
    );
}

function applyFilter(all, filter) {
    if (filter === 'DISMISSED') return all.filter(i => i.dismissed);
    return all.filter(i => !i.dismissed && (filter === 'ALL' || i.severity === filter));
}

export default function InsightsTab({ onAskAI }) {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revalidating, setRevalidating] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('ALL');

    const loadInsights = useCallback(async () => {
        setLoading(true);
        try {
            const res = await InsightService.getAll();
            const all = Array.isArray(res?.data) ? res.data : [];
            return all;
        } catch {
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const applyAndSet = useCallback((all, filter) => {
        setInsights(applyFilter(all, filter));
    }, []);

    // On mount: show cached DB data instantly, then silently revalidate rules in background
    useEffect(() => {
        let cancelled = false;

        // Show cached data right away
        loadInsights().then(all => {
            if (!cancelled) applyAndSet(all, 'ALL');
        });

        // Silently re-run rules; auto-dismisses stale insights and picks up new ones
        setRevalidating(true);
        InsightService.revalidate()
            .then(res => {
                if (!cancelled) {
                    const fresh = Array.isArray(res?.data) ? res.data : [];
                    applyAndSet(fresh, 'ALL');
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setRevalidating(false); });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-filter when filter chip changes (no extra fetch needed)
    useEffect(() => {
        loadInsights().then(all => applyAndSet(all, activeFilter));
    }, [activeFilter, loadInsights, applyAndSet]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshError(null);
        try {
            const res = await InsightService.refresh();
            const fresh = Array.isArray(res?.data) ? res.data : [];
            applyAndSet(fresh, activeFilter);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to regenerate insights.';
            setRefreshError(msg);
        } finally {
            setRefreshing(false);
        }
    };

    const handleDismiss = async (id) => {
        // Optimistic removal
        setInsights(prev => prev.filter(i => i.id !== id));
        try {
            await InsightService.dismiss(id);
        } catch {
            loadInsights().then(all => applyAndSet(all, activeFilter));
        }
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Filter chips row — sticky */}
            <Box
                sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 1,
                    borderBottom: '1px solid #EEF2F6',
                    flexShrink: 0,
                    overflowX: 'auto',
                    display: 'flex',
                    gap: 0.75,
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
            >
                {FILTERS.map(f => {
                    const isActive = activeFilter === f.key;
                    return (
                        <Chip
                            key={f.key}
                            label={f.label}
                            size="small"
                            onClick={() => setActiveFilter(f.key)}
                            sx={{
                                height: 26,
                                fontSize: 11,
                                fontWeight: isActive ? 700 : 500,
                                flexShrink: 0,
                                cursor: 'pointer',
                                bgcolor: isActive ? f.color : 'transparent',
                                color: isActive ? '#fff' : '#64748B',
                                border: `1px solid ${isActive ? f.color : '#E2E8F0'}`,
                                '&:hover': {
                                    bgcolor: isActive ? f.color : `${f.color}18`,
                                    color: isActive ? '#fff' : f.color,
                                    borderColor: f.color,
                                },
                            }}
                        />
                    );
                })}
            </Box>

            {/* Refresh row */}
            <Box
                sx={{
                    px: 2,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    flexShrink: 0,
                    borderBottom: '1px solid #EEF2F6',
                }}
            >
                {!loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {revalidating && (
                            <CircularProgress size={9} sx={{ color: '#94A3B8' }} />
                        )}
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                            {insights.length} insight{insights.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                )}
                <Button
                    size="small"
                    variant="text"
                    startIcon={
                        refreshing
                            ? <CircularProgress size={12} sx={{ color: '#427898' }} />
                            : <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                    }
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    sx={{
                        fontSize: 11,
                        color: '#427898',
                        py: 0.25,
                        px: 1,
                        minHeight: 24,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#EFF6FF' },
                    }}
                >
                    Regenerate insights
                </Button>
            </Box>

            {/* Error banner */}
            {refreshError && (
                <Alert severity="error" onClose={() => setRefreshError(null)} sx={{ mx: 2, mt: 1, fontSize: 12 }}>
                    {refreshError}
                </Alert>
            )}

            {/* Scrollable list */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {loading || refreshing ? (
                    <>
                        <SkeletonCard />
                        <SkeletonCard />
                        <SkeletonCard />
                    </>
                ) : insights.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 8,
                            gap: 1.5,
                            color: '#94A3B8',
                        }}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 36, color: '#CBD5E1' }} />
                        <Typography sx={{ fontSize: 13, color: '#64748B', fontWeight: 600, textAlign: 'center' }}>
                            No insights for this filter.
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}>
                            Click Regenerate to analyse your business data.
                        </Typography>
                    </Box>
                ) : (
                    insights.map(insight => (
                        <InsightCard
                            key={insight.id}
                            insight={insight}
                            onDismiss={handleDismiss}
                            onAskAI={onAskAI}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
}
